import React from 'react';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import queryString from 'query-string';
import { formatCurrency, formatDateTime } from 'utils';
import { duckify, filterDuck } from 'utils/duckify';
import { stripePayouts as stripePayoutsDuck } from 'ducks';
import withPagination from '../../hocs/withPagination';
import { PayoutType } from '../../propTypes';
import ContactList from '../ContactList/ContactList';
import './styles/StripePayoutList.scss';
import { currencyColumn, defaultColumn, linkColumn } from '../ContactList/ColumnDefinitions';
import Placeholder from "../Placeholder";

const availability = (payout) => (
  `Available on ${formatDateTime(payout.arrival_date, 'fns_date', 'UTC')}`
);

const destination = (payout) => {
  if (payout.destination.object === 'card') {
    return `${payout.destination.brand} ${payout.destination.last4}`;
  }
  return payout.destination.bank_name;
};

const getColumns = (location) => [
  linkColumn({
    header: 'Payout ID',
    value: (payout) => payout.id,
    link: (payout) => `${location.pathname.replace(/\/$/, '')}/${payout.id}${location.search}`,
    columnClassNames: 'payout-id'
  }),
  defaultColumn({
    header: 'Date / Time',
    value: (payout) => <td className='payout-datetime'>{formatDateTime(payout.created)}</td>
  }),
  defaultColumn({
    header: 'Bank',
    value: (payout) => <td className='payout-destination'>{destination(payout)}</td>
  }),
  currencyColumn({
    header: 'Amount',
    defaultToZero: true,
    value: 'amount',
    columnClassNames: 'payout-amount'
  }),
  defaultColumn({
    header: 'Status',
    value: 'status',
    columnClassNames: 'payout-status'
  }),
  defaultColumn({
    header: 'Availability',
    value: (payout) => <td className='payout-available'>{availability(payout)}</td>
  })
];

const StripePayoutsList = (props) => {
  const {
    stripePayouts,
    location,
    filters,
    setPage,
    summary,
    loading,
    generateFilters
  } = props;

  const { total, page, perPage } = stripePayouts;
  const totalPages = Math.ceil(total / perPage);
  return (
    <React.Fragment>
      <StripeSummary summary={summary} loading={loading} />

      <div className='stripe-payout-report-space-between'>
        {generateFilters()}

        {totalPages > 0 && (
          <div className='pagination pagination-right stripe-payout-paging-spacing'>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              hideFirstAndLastPageLinks
              onChange={setPage}
            />
          </div>
        )}
      </div>

      <ContactList
        columns={getColumns(location)}
        contacts={stripePayouts}
        className='stripe-payouts'
        emptyMessage={`There are no payouts for the given ${Object.keys(filters).length > 0
          ? 'date range or filters.' : 'date range.'}`}
      />

      {totalPages > 0 && stripePayouts.length > 10 && (
        <div className={'pagination pagination-right'}>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            hideFirstAndLastPageLinks
            onChange={setPage}
          />
        </div>
      )}
    </React.Fragment>
  );
};

StripePayoutsList.propTypes = {
  stripePayouts: PropTypes.arrayOf(PayoutType),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }),
  filters: PropTypes.objectOf(PropTypes.string),
  page: PropTypes.number,
  setPage: PropTypes.func,
  summary: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  loading: PropTypes.bool.isRequired,
  generateFilters: PropTypes.func
};

StripePayoutsList.defaultProps = {
  stripePayouts: [],
  location: {},
  filters: {},
  page: 1,
  setPage: () => false,
  summary: '',
  loading: false,
  generateFilters: () => false,
};

const mapDucksToProps = ({ filters, dateFilter, page, location }) => {
  // We're not currently offering an interface to change `per_page`,
  // but it's "useful" to be able to force paging with minimal data
  const query = queryString.parse(location.search);
  const per_page = query.per_page;

  return {
    stripePayouts: filterDuck(stripePayoutsDuck, {
      ...dateFilter.queryParams,
      filters: JSON.stringify(filters),
      page,
      per_page
    })
  };
};

const StripeSummary = ({ summary, loading }) => {
  const { paid = 0, in_transit = 0, pending = 0 } = summary;
  return (
    <div className={'stripe-payout-overview'}>
      <Placeholder.loading ready={!loading}>
        <React.Fragment>
          <div className={'payout-status'}>
            <div className={'stripe-payout-overview-header'}>Paid</div>
            <div className={'stripe-payout-overview-value'}>{formatCurrency(paid)}</div>
          </div>
          <div className={'payout-date'}>
            <div className={'stripe-payout-overview-header'}>In Transit</div>
            <div className={'stripe-payout-overview-value'}>{formatCurrency(in_transit)}</div>
          </div>
          <div className={'payout-amount'}>
            <div className={'stripe-payout-overview-header'}>Pending</div>
            <div className={'stripe-payout-overview-value'}>{formatCurrency(pending)}</div>
          </div>
        </React.Fragment>
      </Placeholder.loading>
    </div>
  );
};

StripeSummary.propTypes = {
  summary: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  loading: PropTypes.bool.isRequired
};

export default compose(
  withRouter,
  withPagination,
  duckify(mapDucksToProps)
)(StripePayoutsList);
